import { TenantConfig, Environment } from './types';

/**
 * Minimum required version of the create-tenant Terraform module.
 *
 * When editing a tenant, if the current module version is below this threshold
 * (or empty), the user is informed that the module will be upgraded.
 *
 * Update this constant when the TenantConfigurator UI relies on features
 * from a newer module version.
 */
export const MIN_MODULE_VERSION = '2.0.0';

/**
 * Fallback version constraint for the generated HCL. The create flow normally
 * overrides this once GeneralTab fetches the registry and picks the latest
 * major, but this value is what gets used if the registry call fails or for
 * edit-mode upgrades of tenants whose current version is below the minimum.
 *
 * Format is `~> X.0` (two-component) so the major is pinned while minor/patch
 * are allowed to float.
 */
const MIN_MAJOR = parseInt(MIN_MODULE_VERSION.split('.')[0], 10);
export const MODULE_VERSION_CONSTRAINT = `~> ${MIN_MAJOR}.0`;

export const DEFAULT_APIS = [
  'iamcredentials.googleapis.com',
  'iam.googleapis.com',
  'serviceusage.googleapis.com',
  'cloudresourcemanager.googleapis.com',
  'cloudbilling.googleapis.com',
  'billingbudgets.googleapis.com',
];

export function computeExpiresDate(months: number): string {
  const date = new Date();
  date.setMonth(date.getMonth() + months);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function createDefaultEnvironment(): Environment {
  return {
    name: '',
    enable_billing_user_role: false,
    service_account_roles: ['roles/run.admin', 'roles/storage.admin'],
    additional_apis: [],
    labels: [],
    extra: {},
  };
}

export function createDefaultConfig(): TenantConfig {
  const defaultMonths = 6;
  const defaultLifetimeBudget = 3000;

  return {
    business_unit_repo: 'bu-sandbox-1wzye',
    tenant_name: '',
    project_end_months: defaultMonths,
    project_end_date: computeExpiresDate(defaultMonths),
    project_expiry: computeExpiresDate(defaultMonths),
    project_creation: new Date().toISOString().split('T')[0],

    environments: [
      { ...createDefaultEnvironment(), name: 'non-production' },
      { ...createDefaultEnvironment(), name: 'production' },
    ],

    additional_apis: [],

    labels: [],
    topics: [],

    branching_model: 'trunk_based',
    deployment_trigger: 'push',
    template_repo: 'foundations-template',
    share_state: true,
    include_all_branches: false,
    teams: [],
    owner_name: '',
    cost_center: '',
    owner_email: '',
    owner_team: '',
    purpose: '',
    description: '',
    // Budget: lifetime is canonical, monthly is derived
    budget_amount: Math.round(defaultLifetimeBudget / defaultMonths),
    budget_calendar_period: 'MONTH',
    lifetime_budget: defaultLifetimeBudget,
    enable_budget: true,

    enable_gar: false,
    gar_repository_types: [],

    module_version: MODULE_VERSION_CONSTRAINT,
  };
}

export const GAR_REPOSITORY_TYPES = [
  'DOCKER',
  'MAVEN',
  'NPM',
  'PYTHON',
  'APT',
  'YUM',
  'GO',
  'KUBEFLOW_PIPELINES',
];
